
import React, { useEffect, useMemo, useState } from "react"
import { InputAdornment, Stack, TextField, Typography } from "@mui/material"
import { makeStyles } from "@mui/styles"
import clsx from "clsx"
import { capitalizeFirstLetter, COLORS, roundNumber } from "../../utils"
import { useSelector } from "react-redux"
import { getPSESectionSelector } from "../../reducers/Production/ProductionSteps"
import { getSectionOutputsData } from "../../utils/sectionUtils"
import { getPSEStepComponentIngredientsData } from "../../utils/stepComponentUtils"
import { getAllPSEWithCommentsBySection, getPseWithSectionOutputs } from "../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"



const TextFieldWithAdornment = (props) => {
  const isExceeded = props.max && parseFloat(props.value || 0) > parseFloat(props.max)
  return (
    <TextField
      {...props}
      InputProps={{
        endAdornment: (
          props.value ? <InputAdornment position="end" sx={{ "& *": { color: COLORS.BLACK800 } }}>Kg</InputAdornment> : null
        )
      }}
      sx={{
        "& .MuiInput-underline:after": {
          borderColor: isExceeded ? "red !important" : "inherit"
        },
        "& .MuiInput-underline:before": {
          borderColor: isExceeded ? "red !important" : "inherit"
        }
      }}
    />
  )
}


const PSESectionOutput = ({ productionStepExecution, values, onChange, onExpectedTotalChange, readOnly, error, section: sectionProp, label, recipeInfo, keyPrefix }) => {
  const classes = useStyles()
  const pseSectionFromStore = useSelector(getPSESectionSelector)
  const pseSection = sectionProp || pseSectionFromStore
  const [pseWithSectionOutputs, setPseWithSectionOutputs] = React.useState({})
  const allIngredients = getPSEStepComponentIngredientsData({
    stepComponents: productionStepExecution.productionStepSnapshot.stepComponents,
    substituteSupplierItems: productionStepExecution.substitutes,
    expectedProduction: productionStepExecution.expectedProduction,
    coeff: productionStepExecution.coeff,
    productionStepExecution,
  })
  // Total step weight = sum of all ingredient weights (used for percentage-type section outputs)
  const weight = roundNumber(allIngredients.reduce((acc, ing) => acc + (ing.weight || 0), 0), 2)
  const value = values ? (values.sectionOutputWeights ?? {}) : null
  const editMode = Boolean(readOnly || (value && onChange))

  const Total = Object.entries(value || {})
    .filter(([k]) => keyPrefix ? k.startsWith(keyPrefix + "_") : true)
    .reduce((acc, [, curr]) => acc + Number(curr), 0) || 0

  const handleChange = (sectionOutputIndex, e) => {
    if (!onChange) return
    const raw = e.target.value.replace(",", ".")
    onChange({
      ...value,
      [sectionOutputIndex]: raw
    })
  }
  const sectionOutputs = useMemo(() => {
    const sOutputs = getSectionOutputsData(pseSection)
    let outputs = []
    if (editMode) {
      outputs = sOutputs.reduce((acc, output, index) => {
        if (output.type === "percentage") {
          const outputWeight = roundNumber(weight * (output.percentage || 0) / 100)
          acc.push({
            ...output,
            outPutIndex: output.index,
            weight: outputWeight,
            value: `${output.percentage}% - ${output.sectionName}`,
            name: `Partie ${index + 1}`,
            index: (keyPrefix ? keyPrefix + "_" : "") + output.index + "_" + index,
          })
        } else {
          // For "by ingredient" type: show ONE input per partie (not one per ingredient)
          // The total weight is the sum of all ingredients in this partie
          const totalPartieWeight = output.ingredients.reduce((sum, ing) => {
            return sum + roundNumber(ing.netWeight * productionStepExecution.expectedProduction * productionStepExecution.coeff)
          }, 0)
          acc.push({
            ...output,
            outPutIndex: output.index,
            weight: roundNumber(totalPartieWeight, 2),
            value: output.realName || `Partie ${index + 1}`,
            name: output.realName || `Partie ${index + 1}`,
            index: (keyPrefix ? keyPrefix + "_" : "") + output.index + "_" + index,
          })
        }
        return acc
      }, [])
    } else {
      outputs = sOutputs.map((output, index) => {
        return {
          ...output,
          outPutIndex: output.index,
          weight: output.percentage ? roundNumber(weight * (output.percentage || 0) / 100) : output.ingredients.reduce((sum, ing) => {
            return sum + (ing.netWeight * productionStepExecution.expectedProduction * productionStepExecution.coeff)
          }, 0),
          value: output.ingredients.map(ing => ing.supplierItem.name + " " + roundNumber(ing.netWeight * productionStepExecution.expectedProduction * productionStepExecution.coeff, 2) + "kg").join(", "),
          name: output.type === "percentage" ? `Partie ${index + 1}` : output.realName
        }
      })
    }
    return outputs.map(output => {
      const sectionName = pseWithSectionOutputs[output.outPutIndex] ? `Pour ${pseWithSectionOutputs[output.outPutIndex]}` : "À réserver pour le barquettage"
      return {
        ...output,
        weight: roundNumber(output.weight, 2),
        title: editMode ? `${output.value}` : sectionName,
        subtitle: editMode ? sectionName : output.value,
        name: capitalizeFirstLetter(output.name)
      }
    })
  }, [pseSection, weight, productionStepExecution, pseWithSectionOutputs, editMode])

  const TotalExpected = roundNumber(sectionOutputs.reduce((acc, output) => acc + Number(output.weight || 0), 0), 2)

  useEffect(() => {
    if (onExpectedTotalChange) onExpectedTotalChange(TotalExpected)
  }, [TotalExpected])

  useEffect(() => {
    if (!pseSection?.sectionOutputs) return
    (async () => {
      const dataPse = await Promise.all(pseSection.sectionOutputs.map(async output => ({ index: output.index, data: await getPseWithSectionOutputs([output], productionStepExecution.stepProductionDate, productionStepExecution.recipe && productionStepExecution.recipe.objectId || productionStepExecution.recipe.id) })))
      setPseWithSectionOutputs(dataPse.reduce((acc, curr) => {
        if (!curr.data) return acc
        acc[curr.index] = curr.data.get("sectionName")
        return acc
      }, {}))
    })()
  }, [pseSection])

  return (
    <Stack className={clsx(classes.root, editMode && "editMode", readOnly && "readOnly")} spacing={editMode ? 0 : 2} >
      {recipeInfo && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 600, fontSize: 18, color: COLORS.BLACK800, padding: "4px 8px", borderRadius: 8, border: `1px solid ${COLORS.BLACK_200}`, backgroundColor: readOnly ? COLORS.WHITE : COLORS.DARK_BLUE_20 }}>{recipeInfo.uniqueCode}</span>
          <span style={{ fontSize: 18, lieHeight: 24, color: COLORS.BLACK800 }}>{recipeInfo.recipeName}</span>
        </div>
      )}
      {!editMode && <div style={{ marginBottom: 8 }}>{label || "Division de la sauce"} : <span className={clsx(classes.badge, classes.badgeWhite)}>{sectionOutputs.length} parties</span> </div>}
      {
        sectionOutputs.map((output, index) =>
          <div key={index} className={classes.section} style={{ backgroundColor: readOnly ? COLORS.BACKGROUND_GREY : COLORS.DARK_BLUE_20 }}>
            <div style={{ flex: 1 }}>
              <div className="section-header" style={{ marginBottom: "12px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                <span className={clsx(classes.badge, "name-badge")}>{output.name}</span>
                {output.title ? <span style={{ color: COLORS.BLACK800, fontSize:"18px", lineHeight:"24px", fontWeight: 400 }}> - {output.title}</span> : null}
              </div>
              <span className="section-subtitle" style={{ color: COLORS.BLACK_600, fontSize: "18px", lineHeight: "24px" }}>
                {output.subtitle}
              </span>
            </div>
            {editMode && <Stack direction="row" className={classes.stackForm}>
              <Stack sx={{ marginLeft: 6, minWidth: 80 }}>
                <Typography sx={{ color: COLORS.BLACK_600, fontSize: "16px", lineHeight: "24px" }}>Poids prévu</Typography>
                <Typography className={classes.input} sx={{ fontWeight: 500, fontSize: "16px", lineHeight: "24px"  }}>
                  {output.weight} kg
                </Typography>
              </Stack>
              <Stack className={classes.stackStyle} />
              {readOnly
                ? <Stack sx={{ minWidth: 80 }}>
                    <Typography sx={{ color: COLORS.BLACK_600,  fontSize: "16px", lineHeight: "24px" }}>Poids pesé</Typography>
                    <Typography className={classes.input} sx={{ fontWeight: 500, fontSize: "16px", lineHeight: "24px" }}>
                      {value ? value[`${output.index}`] || "" : ""} kg
                    </Typography>
                  </Stack>
                : <Stack sx={{ width: 107, justifyContent: "flex-end !important" }}>
                    <TextFieldWithAdornment
                      label="Poids pesé"
                      variant="standard"
                      name="netWeight"
                      max={output.weight}
                      inputProps={{
                        inputMode: "decimal",
                        pattern: "[0-9]*[.,]?[0-9]*"
                      }}
                      InputLabelProps={{
                        sx: {
                          "&.MuiInputLabel-shrink": {
                            fontSize: "16px",
                            lineHeight: "1.2",
                            transform: "translate(0px, -10px) scale(1)",
                            color: COLORS.BLACK_600
                          },
                        },
                      }}
                      value={value ? value[`${output.index}`] || "" : ""}
                      onChange={(e) => handleChange(output.index, e)}
                      type="text"
                      onWheel={(e) => e.target?.blur()}
                    />
                  </Stack>
              }
            </Stack>}
          </div>
        )
      }
      {editMode &&
        <div className={classes.section} style={{ marginTop: 0, backgroundColor: readOnly ? COLORS.BACKGROUND_GREY : COLORS.DARK_BLUE_20 }}>
          <div style={{ flex: 1 }} className="flexRow center">
            <b className={classes.titleSection}>Poids total attribué</b>
          </div>
          <Stack direction="row" className={classes.stackForm}>
            <Stack sx={{ minWidth: 80 }}>
              <Typography sx={{ color: COLORS.BLACK_600, fontSize: 14 }}>Poids prévu</Typography>
              <Typography sx={{ fontWeight: 500 }}>
                {TotalExpected} kg
              </Typography>
            </Stack>
            <Stack className={classes.stackStyle}>
              /
            </Stack>
            <Stack sx={{ minWidth: 80 }}>
              <Typography sx={{ color: COLORS.BLACK_600, fontSize: 14 }}>Poids pesé</Typography>
              <Typography sx={{ fontWeight: 500, color: error ? "red" : "inherit" }}>
                {Total} kg
              </Typography>
            </Stack>
          </Stack>
        </div>}
    </Stack >
  )
}

const useStyles = makeStyles(() => ({
  root: {
    borderRadius: 12,
    backgroundColor: COLORS.DARK_BLUE_100,
    padding: 16,
    "&.editMode": {
      padding: 0,
      backgroundColor: "initial",
      gap: 8,
      "& .name-badge": {
        margin: 0,
        backgroundColor: COLORS.WHITE,
        border: `1px solid ${COLORS.DARK_BLUE_100}`,
        color: COLORS.BLACK800,
        fontWeight: 500,
        padding: "0px 16px",
        borderRadius: "43px",
        fontSize: "18px",
        lineHeight: "24px"
      },
      "&.readOnly": {
        "& .name-badge": {
          backgroundColor: COLORS.WHITE,
          border: "1px solid #E0E0E0",
          color: COLORS.BLACK_600,
          marginTop: 0,
        padding: "0px 16px",
          borderRadius: "43px",
          fontSize: "18px",
          lineHeight: "24px"
        }
      }
    }
  },
  section: {
    padding: 16,
    borderRadius: 8,
    backgroundColor: COLORS.DARK_BLUE_20,
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  rightIcon: {
    color: COLORS.PRIMARY_BLUE_200,
    fontSize: 20,
    position: "relative",
    top: 4,
  },
  titleSection: {
    fontWeight: 500,
    fontSize: "18px", 
    lineHeight: "24px"
  },
  badge: {
    display: "inline-block",
    marginLeft: 8,
    padding: "0 16px",
    borderRadius: 16,
    backgroundColor: COLORS.DARK_BLUE_200,
    color: COLORS.WHITE
  },
  badgeWhite: {
    marginLeft: 4,
    backgroundColor: COLORS.DARK_BLUE_20,
    color: COLORS.BLACK_600
  },
  stackStyle: {
    width: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end !important",
  },
  stackForm: {
    height: 60,
    "& > div": {
      justifyContent: "space-between",
    }
  },
  textField: {
    "& input": {
      height: "20px",
      marginBottom: "2px",
    }
  }
}
))


export { PSESectionOutput }

/**
 * Wrapper that handles both regular and reusable (merged) PSEs.
 * For merged PSEs, each block gets its own sectionOutputWeights from sectionOutputWeightsByPSE,
 * keyed by the individual PSE objectId. Each PSESectionOutput uses simple keys
 * (sectionOutputIndex_ingredientId) with no keyPrefix — matching the classic PSE format exactly.
 *
 * Props:
 *   - values / onChange: used for classic (non-merged) PSEs
 *   - sectionOutputWeightsByPSE: { [pseObjectId]: { [sectionOutputIndex_ingredientId]: value } }
 *     used for merged PSEs; onChange receives (pseObjectId, newWeightsForThatPSE)
 */
const PSESectionOutputWrapper = ({ productionStepExecution, values, onChange, onExpectedTotalChange, sectionOutputWeightsByPSE, onChangeMerged, readOnly, error }) => {
  const mergedPSEs = productionStepExecution.mergedProductionStepExecutions

  const [mergedSections, setMergedSections] = useState([])

  useEffect(() => {
    if (!mergedPSEs || mergedPSEs.length === 0) return
    ;(async () => {
      const sections = await Promise.all(
        mergedPSEs.map(async (merged) => {
          if (!merged.section) return null
          const result = await getAllPSEWithCommentsBySection(
            merged.section.objectId,
            productionStepExecution.stepProductionDate,
            { withSection: true }
          )
          return result ? result.section : null
        })
      )
      setMergedSections(sections)
    })()
  }, [])

  if (mergedPSEs && mergedPSEs.length > 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {mergedPSEs.map((merged, index) => {
          const mergedSection = mergedSections[index]
          // Only show sectionOutput for merged entries that are the last step of their section
          // (i.e. no ulteriorStep), matching the same rule as classic PSEs
          if (!mergedSection || !mergedSection.sectionOutputs?.length || merged.ulteriorStep) return null
          const pseObjectId = merged.productionStepExecution?.objectId || String(index)
          const mergedPseData = {
            ...merged,
            stepProductionDate: productionStepExecution.stepProductionDate,
          }
          // Each merged block gets its own clean sectionOutputWeights sub-object.
          // Keys are plain {sectionOutputIndex}_{ingredientId} — same as classic PSEs.
          // In readOnly, read from the individual PSE's saved sectionOutputWeights.
          const perPseWeights = readOnly
            ? (merged.productionStepExecution?.sectionOutputWeights ?? {})
            : (sectionOutputWeightsByPSE?.[pseObjectId] ?? {})
          const mergedValues = { sectionOutputWeights: perPseWeights }
          const mergedOnChange = onChangeMerged
            ? (newWeights) => onChangeMerged(pseObjectId, newWeights)
            : null
          return (
            <div key={pseObjectId}>
              <PSESectionOutput
                productionStepExecution={mergedPseData}
                values={mergedValues}
                onChange={mergedOnChange}
                readOnly={readOnly}
                error={error}
                section={mergedSection}
                label={`Division de la sauce pour ${merged.uniqueCode}`}
                recipeInfo={{ uniqueCode: merged.uniqueCode, recipeName: merged.recipeName }}
              />
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <PSESectionOutput
      productionStepExecution={productionStepExecution}
      values={values}
      onChange={onChange}
      onExpectedTotalChange={onExpectedTotalChange}
      readOnly={readOnly}
      error={error}
    />
  )
}

export default PSESectionOutputWrapper